import { useState, useRef } from 'react';
import { Upload, X, Loader2, Link as LinkIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import apiClient from '@/utils/api';

export default function ImageUpload({ 
  value, 
  onChange, 
  label = "Image",
  required = false,
  multiple = false,
  preview = true 
}) {
  const [uploading, setUploading] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const fileInputRef = useRef(null);

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      
      if (multiple) {
        const uploadPromises = files.map(async (file) => {
          const formData = new FormData();
          formData.append('file', file);
          const response = await apiClient.post('/admin/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          // Convert relative URL to full URL
          const relativeUrl = response.data.url;
          return relativeUrl.startsWith('http') ? relativeUrl : `${BACKEND_URL}${relativeUrl}`;
        });
        
        const urls = await Promise.all(uploadPromises);
        const currentUrls = value ? value.split(',').map(u => u.trim()) : [];
        const newUrls = [...currentUrls, ...urls].join(', ');
        onChange(newUrls);
        toast.success(`${files.length} image(s) uploaded successfully!`);
      } else {
        const formData = new FormData();
        formData.append('file', files[0]);
        const response = await apiClient.post('/admin/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        // Convert relative URL to full URL
        const relativeUrl = response.data.url;
        const fullUrl = relativeUrl.startsWith('http') ? relativeUrl : `${BACKEND_URL}${relativeUrl}`;
        onChange(fullUrl);
        toast.success('Image uploaded successfully!');
      }
    } catch (error) {
      toast.error('Failed to upload image');
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleUrlAdd = () => {
    if (!urlInput.trim()) {
      toast.error('Please enter a valid URL');
      return;
    }

    // Basic URL validation
    try {
      new URL(urlInput.trim());
    } catch (e) {
      toast.error('Please enter a valid URL');
      return;
    }

    if (multiple) {
      const currentUrls = value ? value.split(',').map(u => u.trim()) : [];
      const newUrls = [...currentUrls, urlInput.trim()].join(', ');
      onChange(newUrls);
    } else {
      onChange(urlInput.trim());
    }
    
    setUrlInput('');
    setShowUrlInput(false);
    toast.success('Image URL added successfully!');
  };

  const removeImage = (indexToRemove) => {
    if (multiple) {
      const urls = value.split(',').map(u => u.trim()).filter((_, index) => index !== indexToRemove);
      onChange(urls.join(', '));
    } else {
      onChange('');
    }
  };

  const getImageUrls = () => {
    if (!value) return [];
    if (multiple) {
      return value.split(',').map(u => u.trim()).filter(Boolean);
    }
    return [value];
  };

  const imageUrls = getImageUrls();

  return (
    <div className="space-y-3">
      <Label>{label} {required && <span className="text-red-500">*</span>}</Label>
      <p className="text-xs text-gray-500">Upload files (PNG, WebP) or paste image URLs</p>

      <div className="space-y-3">
        {/* Upload Button */}
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".png,.webp"
            multiple={multiple}
            onChange={handleFileUpload}
            className="hidden"
            id={`file-upload-${label}`}
          />
          <Button
            type="button"
            variant="outline"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
            className="flex-1"
          >
            {uploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                Upload File
              </>
            )}
          </Button>
          
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowUrlInput(!showUrlInput)}
            className="flex-1"
          >
            <LinkIcon className="mr-2 h-4 w-4" />
            Use URL
          </Button>
        </div>

        {/* URL Input Section */}
        {showUrlInput && (
          <div className="flex items-center gap-2 p-3 bg-gray-50 rounded border">
            <Input
              type="url"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder="https://example.com/image.png"
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleUrlAdd())}
            />
            <Button
              type="button"
              onClick={handleUrlAdd}
              size="sm"
              className="bg-blue-600 hover:bg-blue-700"
            >
              Add
            </Button>
            <Button
              type="button"
              onClick={() => {
                setShowUrlInput(false);
                setUrlInput('');
              }}
              size="sm"
              variant="outline"
            >
              Cancel
            </Button>
          </div>
        )}

        {preview && imageUrls.length > 0 && (
          <div className={`grid gap-3 ${multiple ? 'grid-cols-3' : 'grid-cols-1'}`}>
            {imageUrls.map((url, index) => (
              <div key={index} className="relative group">
                <img
                  src={url}
                  alt={`Preview ${index + 1}`}
                  className="w-full h-32 object-cover rounded border"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/300x200?text=Image+Not+Found';
                  }}
                />
                <button
                  type="button"
                  onClick={() => removeImage(index)}
                  className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X size={14} />
                </button>
                {/* Show URL type indicator */}
                <div className="absolute bottom-1 left-1 bg-black/70 text-white text-xs px-2 py-1 rounded">
                  {url.startsWith('http') ? (url.includes('/api/uploads') ? '📁 Uploaded' : '🔗 URL') : '📁 Uploaded'}
                </div>
              </div>
            ))}
          </div>
        )}

        {value && !preview && (
          <p className="text-sm text-gray-600">
            {multiple ? `${imageUrls.length} image(s) selected` : 'Image selected'}
          </p>
        )}
      </div>
    </div>
  );
}
